import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getManufacturers } from '../../clients/manufacturer_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import ManufacturerModal from '../../components/Manufacturer/ManufacturerModal';
import InfiniteScroll from 'react-infinite-scroller';
import { showToast } from '../../config_and_helpers/helpers';
import { Spinner } from 'react-bootstrap';

import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import ManufacturerTable from '../../components/Manufacturer/ManufacturerTable';
import {
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';

// Module-level variable to track initial fetch across re-renders
let hasInitialFetch = false;

/**
 * Context to share manufacturer data and setter within children.
 */
export const ManufacturerContext = React.createContext();

/**
 * ManufacturersPage is a component that handles state, context, and UI logic for displaying a list of manufacturers.
 * It supports debounced text search, supplier-based filtering, and paginated infinite scrolling.
 * Server-side filtering is applied based on search input and supplier ID.
 *
 * @returns {JSX.Element} - JSX element containing the manufacturer search bar, filter UI, and infinite scroll list
 */
const ManufacturersPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const isMountedRef = useRef(false); // Used to avoid state updates on unmounted component
  const isLoadingRef = useRef(false); // Used to prevent overlapping fetches
  const debounceTimeoutRef = useRef(null); // Used for debounced search
  const currentRequestRef = useRef(null); // Used to cancel previous requests
  const hasMoreRef = useRef(true);

  const [manufacturers, setManufacturers] = useState([]); // Main list of manufacturers
  const [supplier, setSupplier] = useState(''); // Selected supplier filter
  const [supplierSelectList, setSupplierSelectList] = useState([]); // Supplier select list
  const [searchInput, setSearchInput] = useState(''); // Text-based search input
  const [nextPageUrl, setNextPageUrl] = useState(null); // Pagination cursor
  const [hasMore, setHasMore] = useState(true); // Whether more data is available
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [isSearching, setIsSearching] = useState(false); // Search-specific loading state
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Initialize component and fetch initial list
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetch) {
      hasInitialFetch = true;

      // Fetch initial manufacturers
      setIsLoading(true);
      isLoadingRef.current = true;

      getManufacturers(token, setManufacturers, {
        searchInput: '',
        supplierId: '',
        nextPage: null,
        sortBy: 'name',
        sortDirection: 'asc',
      }).then(response => {
        if (!isMountedRef.current) return;

        if (response?.success) {
          setNextPageUrl(response.reachedEnd ? null : response.nextPage);
          setHasMore(!response.reachedEnd);
          hasMoreRef.current = !response.reachedEnd;
        } else {
          showToast(
            'An unexpected error occurred. Please try again',
            'error',
            'top-center',
            3000
          );
        }

        isLoadingRef.current = false;
        setIsLoading(false);
      });
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [token]);

  // Fetch supplier select list on component mount
  useEffect(() => {
    getSupplierSelectList(token, setSupplierSelectList);
  }, [token]);

  /**
   * Fetch manufacturers from the API with optional filters and pagination.
   * @param {Object} params
   * @param {string} params.searchValue - Text search term
   * @param {string|null} params.nextPage - URL to fetch the next page
   * @param {string} params.supplierId - Supplier ID to filter by
   */
  const fetchManufacturers = useCallback(
    (
      {
        searchValue = '',
        nextPage = null,
        supplierId = '',
        sortByValue = 'name',
        sortDirectionValue = 'asc',
      } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue,
        supplierId,
        nextPage,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
      };

      // Supersede in-flight request only when starting a replacement fetch
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }

      // Set loading state
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        // Clear existing manufacturers for new search
        setManufacturers([]);
      }
      isLoadingRef.current = true;

      // Create a cancelable promise
      const controller = new AbortController();
      currentRequestRef.current = controller;

      getManufacturers(token, setManufacturers, {
        searchInput: searchValue,
        supplierId: supplierId,
        nextPage: nextPage,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
        signal: controller.signal,
      })
        .then(response => {
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          const responseMeta = {
            success: response?.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (!isMountedRef.current) return;

          if (response?.success) {
            setNextPageUrl(response.reachedEnd ? null : response.nextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'manufacturers',
            response?.success ? 'success' : 'failure',
            {
              trigger,
              params,
              responseMeta,
            }
          );

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('manufacturers', trigger, error, { params });

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        });
    },
    [token]
  );

  // Cleanup effect for debounce timeout and requests
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
    };
  }, []);

  /**
   * Smart debounced search input handler with immediate UI feedback
   */
  const debouncedFetch = useCallback(
    (val, supplier, sortByValue, sortDirectionValue) => {
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Immediate UI feedback: clear results and show searching state
      if (val.length >= 2) {
        setManufacturers([]);
        setIsSearching(true);
      }

      // Only search if we have 2+ characters or empty string
      if (isRunnableListSearchQuery(val)) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchManufacturers(
            {
              searchValue: val,
              nextPage: null,
              supplierId: supplier,
              sortByValue,
              sortDirectionValue,
            },
            true
          ); // newSearch = true for search operations
        }, 300); // Reduced from 500ms to 300ms for better responsiveness
      } else if (isPartialListSearchQuery(val)) {
        setManufacturers([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchManufacturers]
  );

  /**
   * Handles changes in the text search input
   * @param {string} val - New search term
   */
  const handleSearchInput = val => {
    setSearchInput(val);
    debouncedFetch(val, supplier, sortBy, sortDirection);
  };

  /**
   * Handles changes in the selected supplier filter
   * @param {string} val - New supplier ID
   */
  const handleSupplierChange = val => {
    setSupplier(val);
    fetchManufacturers(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierId: val,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    ); // newSearch = true for filter changes
  };

  const handleSortByChange = (value, direction = 'asc') => {
    setSortBy(value);
    setSortDirection(direction);
    fetchManufacturers(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierId: supplier,
        sortByValue: value,
        sortDirectionValue: direction,
      },
      true
    );
  };

  /**
   * Clears the text input value
   */
  const resetSearchValue = () => setSearchInput('');

  return (
    <GlassWorkspaceShell theme="rose" wide>
    <ManufacturerContext.Provider
      value={{
        manufacturers,
        setManufacturers,
      }}
    >
      <div className="glass-toolbar">
      <GenericSearchFilter
        entityType="suppliers"
        placeHolderLabel="Supplier"
        searchInput={searchInput}
        onSearchInput={handleSearchInput}
        onEntityChange={handleSupplierChange}
        entity={supplier}
        supplierSelectList={supplierSelectList}
        ModalComponent={
          isViewer ? null : (
          <ManufacturerModal
            onSuccessfulSubmit={
              () =>
                fetchManufacturers(
                  {
                    searchValue: searchInput,
                    supplierId: supplier,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  true
                ) // newSearch = true for refresh after modal submit
            }
          />
          )
        }
      />
      </div>

      {(isLoading && manufacturers.length === 0) || isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 text-muted">Searching manufacturers...</div>
          )}
        </div>
      ) : (
        <>
          {/* No results state */}
          {!isLoading && !isSearching && manufacturers.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <span
                  role="img"
                  aria-label="no results"
                  style={{ fontSize: '3rem' }}
                >
                  🏭
                </span>
              </div>
              <h5 className="text-muted mb-2">No manufacturers found</h5>
              <p className="text-muted mb-3">
                {searchInput.length >= 2
                  ? `No manufacturers match your search for "${searchInput}"`
                  : 'No manufacturers available'}
              </p>
              {searchInput.length >= 2 && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setSearchInput('');
                    fetchManufacturers(
                      {
                        searchValue: '',
                        nextPage: null,
                        supplierId: supplier,
                        sortByValue: sortBy,
                        sortDirectionValue: sortDirection,
                      },
                      true
                    );
                  }}
                >
                  <span role="img" aria-label="clear search">
                    🗑️
                  </span>{' '}
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <InfiniteScroll
              pageStart={0}
              loadMore={() => {
                if (
                  isLoadingRef.current ||
                  !hasInitialFetch ||
                  !nextPageUrl ||
                  isPartialListSearchQuery(searchInput)
                ) {
                  return;
                }

                const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                  search: searchInput,
                  supplier_id: supplier,
                  sort_by: sortBy,
                  sort_direction: sortDirection,
                });
                if (mismatch === null) {
                  logListSearchError(
                    'manufacturers',
                    'next-page-url-stale-check',
                    new Error('Failed to parse nextPageUrl'),
                    { nextPageUrl, searchInput, supplier, sortBy, sortDirection }
                  );
                  setHasMore(false);
                  hasMoreRef.current = false;
                  setNextPageUrl(null);
                  return;
                }
                if (mismatch.mismatched) {
                  logListSearchEvent('manufacturers', 'next-page-url-stale-mismatch', {
                    nextPageUrl,
                    expected: mismatch.expected,
                    actual: mismatch.actual,
                  });
                  setHasMore(false);
                  hasMoreRef.current = false;
                  setNextPageUrl(null);
                  return;
                }

                fetchManufacturers(
                  {
                    searchValue: searchInput,
                    nextPage: nextPageUrl,
                    supplierId: supplier,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  false
                ); // newSearch = false for pagination
              }}
              hasMore={hasMore}
              loader={
                <div key="manu-loader" className="text-center py-2">
                  <Spinner animation="border" size="sm" role="status" />
                  <div className="mt-1">
                    <small className="text-muted">
                      Loading more manufacturers...
                    </small>
                  </div>
                </div>
              }
              useWindow={true}
              threshold={INFINITE_SCROLL_THRESHOLD_PX}
            >
              <div className="glass-table-wrap">
              <ManufacturerTable
                manufacturerList={manufacturers}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSortByChange}
                clearSearchValue={resetSearchValue}
                handleDelete={
                  () =>
                    fetchManufacturers(
                      {
                        searchValue: searchInput,
                        supplierId: supplier,
                        sortByValue: sortBy,
                        sortDirectionValue: sortDirection,
                      },
                      true
                    ) // newSearch = true for refresh after delete
                }
              />
              </div>
            </InfiniteScroll>
          )}
        </>
      )}
    </ManufacturerContext.Provider>
    </GlassWorkspaceShell>
  );
};

export default ManufacturersPage;
